import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Truck, Package, Award, Heart, MessageCircle, Mail, Phone, MapPin,
  Loader2, Send, ChevronDown, ChevronRight, Facebook, Instagram, ShieldCheck,
  Clock, RefreshCcw, ArrowLeft,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/* ─────────────────────── shared shell ─────────────────────── */
export function StaticPage({ eyebrow, title, subtitle, children }) {
  return (
    <div className="pt-24 pb-16 min-h-screen bg-[#FDFBF7]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-[#064E3B] mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
        {eyebrow && <span className="inline-block text-xs uppercase tracking-widest text-[#C2410C] font-bold mb-3">{eyebrow}</span>}
        <h1
          className="text-4xl md:text-5xl font-bold text-[#064E3B] mb-3"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {title}
        </h1>
        {subtitle && <p className="text-lg text-gray-600 max-w-2xl mb-10">{subtitle}</p>}
        <div className="space-y-8">{children}</div>
      </div>
    </div>
  );
}

const Card = ({ children, className = '', ...rest }) => (
  <div className={`bg-white rounded-3xl border border-gray-100 p-6 md:p-8 ${className}`} {...rest}>{children}</div>
);

/* ─────────────────────── ABOUT ─────────────────────── */
export function AboutPage() {
  const values = [
    { icon: Award, title: 'Quality', body: 'We source only the best products so every meal you cook lives up to what you remember.' },
    { icon: ShieldCheck, title: 'Authenticity', body: 'Real Nigerian and African brands — nothing generic, nothing substituted.' },
    { icon: Heart, title: 'Customer focus', body: 'Small team, real people. Every order is a conversation, not a transaction.' },
    { icon: Truck, title: 'Fast delivery', body: 'UK-wide dispatch in 24–48 hours. Free shop pick-up in South Shields.' },
  ];
  const team = [
    { name: 'SOTUBO Emmanuel', role: 'CEO', img: '/assets/Emma.jpg' },
    { name: 'SOTUBO Ibitayo',  role: 'Manager', img: '/assets/CEO.jpg' },
    { name: 'Dammy',           role: 'IT & Marketing Director', img: '/assets/Dammy.jpg' },
    { name: 'Tammy',           role: 'Video Editor & Marketing', img: '/assets/Tammy.jpg' },
  ];

  return (
    <StaticPage
      eyebrow="Our Story"
      title="Bringing home to your kitchen"
      subtitle="TishoFood was born out of a passion for authentic Nigerian cuisine and the desire to share it with the diaspora across the UK."
    >
      <Card data-testid="about-mission">
        <h2 className="text-2xl font-bold text-[#064E3B] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Our mission</h2>
        <p className="text-gray-700 leading-relaxed">
          We serve customers all over the UK from our shop in South Shields, and are thrilled that we're able to turn our passion into a thriving online store. Our mission is to make it easy for anyone, anywhere, to enjoy authentic Nigerian and African food — with fair pricing, real products, and quick UK-wide delivery.
        </p>
      </Card>

      <div>
        <h2 className="text-2xl font-bold text-[#064E3B] mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Our core values</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-values">
          {values.map((v) => (
            <Card key={v.title} className="text-center">
              <div className="w-12 h-12 rounded-2xl bg-[#064E3B]/10 text-[#064E3B] mx-auto flex items-center justify-center mb-3">
                <v.icon className="w-6 h-6" />
              </div>
              <h3 className="font-semibold mb-1">{v.title}</h3>
              <p className="text-sm text-gray-600">{v.body}</p>
            </Card>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl font-bold text-[#064E3B] mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif" }}>Meet the team</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="about-team">
          {team.map((t) => (
            <Card key={t.name} className="text-center" data-testid={`team-${t.name.split(' ').pop().toLowerCase()}`}>
              <div className="w-24 h-24 rounded-full overflow-hidden mx-auto mb-3 bg-gray-100">
                <img src={t.img} alt={t.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <h3 className="font-semibold">{t.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{t.role}</p>
            </Card>
          ))}
        </div>
      </div>

      <Card className="bg-gradient-to-br from-[#064E3B] to-[#053D2E] text-white text-center">
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Have questions?</h2>
        <p className="text-white/85 max-w-md mx-auto mb-6">We'd love to hear from you. Reach out and one of us will get back to you personally.</p>
        <div className="flex gap-3 justify-center flex-wrap">
          <Link to="/contact" className="inline-flex items-center gap-2 px-6 py-3 bg-[#EAB308] text-[#064E3B] rounded-full font-semibold hover:bg-[#F59E0B] transition">
            <Mail className="w-4 h-4" /> Contact us
          </Link>
          <a href="https://wa.me/4407519935205" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-full font-semibold hover:bg-[#128C7E] transition">
            <MessageCircle className="w-4 h-4" /> WhatsApp
          </a>
        </div>
      </Card>
    </StaticPage>
  );
}

/* ─────────────────────── CONTACT ─────────────────────── */
export function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState({ loading: false, ok: false, error: '' });

  const submit = async (e) => {
    e.preventDefault();
    setStatus({ loading: true, ok: false, error: '' });
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setStatus({ loading: false, ok: true, error: '' });
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      setStatus({ loading: false, ok: false, error: err.message });
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10';

  return (
    <StaticPage eyebrow="Get in touch" title="Contact us" subtitle="Questions about an order, a product, or working together? Send us a message and we'll come back to you within one business day.">
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <Card data-testid="contact-form-card">
          {status.ok && (
            <div className="mb-4 p-4 rounded-xl bg-emerald-50 text-emerald-700 text-sm" data-testid="contact-success">
              Thanks — your message has been received. We'll be in touch shortly.
            </div>
          )}
          {status.error && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 text-red-700 text-sm" data-testid="contact-error">{status.error}</div>
          )}
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label>
                <span className="block text-sm text-gray-700 mb-1">Name *</span>
                <input required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name" />
              </label>
              <label>
                <span className="block text-sm text-gray-700 mb-1">Email *</span>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email" />
              </label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <label>
                <span className="block text-sm text-gray-700 mb-1">Phone (optional)</span>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="contact-phone" />
              </label>
              <label>
                <span className="block text-sm text-gray-700 mb-1">Subject *</span>
                <input required minLength={3} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="contact-subject" />
              </label>
            </div>
            <label>
              <span className="block text-sm text-gray-700 mb-1">Message *</span>
              <textarea required minLength={10} rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={inputCls} data-testid="contact-message" />
            </label>
            <button type="submit" disabled={status.loading} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#064E3B] text-white rounded-xl font-semibold hover:bg-[#065F46] transition disabled:opacity-60" data-testid="contact-submit">
              {status.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send message
            </button>
          </form>
        </Card>

        <Card>
          <h3 className="font-bold text-[#064E3B] mb-4">Reach us directly</h3>
          <ul className="space-y-4 text-sm">
            <li className="flex gap-3"><MapPin className="w-4 h-4 text-[#064E3B] mt-0.5" /><span>49 Westoe Rd, South Shields<br />NE33 4LU, United Kingdom</span></li>
            <li className="flex gap-3"><Phone className="w-4 h-4 text-[#064E3B] mt-0.5" /><a href="tel:+4407519935205" className="hover:text-[#064E3B]">+44 77519 935205</a></li>
            <li className="flex gap-3"><Mail className="w-4 h-4 text-[#064E3B] mt-0.5" /><a href="mailto:orders@tishofood.co.uk" className="hover:text-[#064E3B]">orders@tishofood.co.uk</a></li>
            <li className="flex gap-3"><Clock className="w-4 h-4 text-[#064E3B] mt-0.5" /><span>Mon–Sat 10am–7pm<br />Sun 11am–5pm</span></li>
          </ul>
          <div className="mt-6 pt-6 border-t border-gray-100 flex gap-2">
            <a href="https://facebook.com/tishofood" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-[#064E3B]/5 hover:bg-[#064E3B]/10 text-[#064E3B]"><Facebook className="w-4 h-4" /></a>
            <a href="https://instagram.com/tishofood" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-[#064E3B]/5 hover:bg-[#064E3B]/10 text-[#064E3B]"><Instagram className="w-4 h-4" /></a>
            <a href="https://wa.me/4407519935205" target="_blank" rel="noopener noreferrer" className="p-2.5 rounded-xl bg-[#25D366]/10 hover:bg-[#25D366]/20 text-[#25D366]"><MessageCircle className="w-4 h-4" /></a>
          </div>
        </Card>
      </div>

      {/* Map — 49 Westoe Rd, South Shields, NE33 4LU (approx. 54.9928, -1.4278) */}
      <Card className="!p-0 overflow-hidden" data-testid="contact-map-card">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#064E3B]/10 text-[#064E3B] flex items-center justify-center"><MapPin className="w-5 h-5" /></div>
            <div>
              <h3 className="font-semibold text-[#064E3B]">Visit the shop</h3>
              <p className="text-sm text-gray-500">49 Westoe Rd, South Shields, NE33 4LU</p>
            </div>
          </div>
          <a
            href="https://www.google.com/maps/dir/?api=1&destination=49+Westoe+Rd+South+Shields+NE33+4LU"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#064E3B] text-white rounded-full text-xs sm:text-sm font-medium hover:bg-[#065F46] transition whitespace-nowrap"
            data-testid="contact-directions"
          >
            <ChevronRight className="w-4 h-4" /> Get directions
          </a>
        </div>
        <iframe
          title="TishoFood shop location — 49 Westoe Rd, South Shields, NE33 4LU"
          src="https://www.openstreetmap.org/export/embed.html?bbox=-1.4358,54.9880,-1.4198,54.9976&layer=mapnik&marker=54.9928,-1.4278"
          className="w-full h-[360px] border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          data-testid="contact-map-iframe"
        />
        <div className="p-3 text-center text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
          <a href="https://www.openstreetmap.org/?mlat=54.9928&mlon=-1.4278#map=17/54.9928/-1.4278" target="_blank" rel="noopener noreferrer" className="hover:text-[#064E3B]">
            View a larger map on OpenStreetMap →
          </a>
        </div>
      </Card>
    </StaticPage>
  );
}

/* ─────────────────────── DELIVERY ─────────────────────── */
export function DeliveryPage() {
  const brackets = [
    { range: 'Shop pick-up', price: 'Free', note: 'Collect from our South Shields shop.' },
    { range: 'DH / SR / NE postcodes (local)', price: '£6.00', note: 'Flat local rate regardless of weight.' },
    { range: 'Up to 20 kg', price: '£7.99', note: 'Rest of UK.' },
    { range: '20.01 – 30 kg', price: '£10.00', note: 'Rest of UK.' },
    { range: '30.01 – 40 kg', price: '£15.00', note: 'Rest of UK.' },
    { range: '40.01 – 60 kg', price: '£25.00', note: 'Rest of UK.' },
    { range: 'Over 60 kg', price: 'Contact us', note: 'We\'ll arrange a bespoke courier quote.' },
  ];
  return (
    <StaticPage eyebrow="Shipping" title="Delivery & pick-up" subtitle="Weight-based UK delivery with a flat rate for local orders, or free collection from our shop.">
      <Card>
        <h2 className="text-xl font-bold text-[#064E3B] mb-4">How our rates work</h2>
        <p className="text-gray-700 mb-6">Your total shipping is calculated live from the weight of every item in your cart plus your postcode. You'll see the exact bracket and price on the checkout page before you pay.</p>
        <div className="overflow-hidden rounded-2xl border border-gray-100" data-testid="delivery-brackets-table">
          <table className="w-full text-sm">
            <thead className="bg-[#064E3B] text-white text-left">
              <tr><th className="p-3">Bracket</th><th className="p-3">Price</th><th className="p-3">Notes</th></tr>
            </thead>
            <tbody>
              {brackets.map((b, i) => (
                <tr key={b.range} className={i % 2 ? 'bg-gray-50' : ''}>
                  <td className="p-3 font-medium">{b.range}</td>
                  <td className="p-3 text-[#064E3B] font-bold">{b.price}</td>
                  <td className="p-3 text-gray-500">{b.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="w-10 h-10 rounded-xl bg-[#064E3B]/10 text-[#064E3B] flex items-center justify-center mb-3"><Truck className="w-5 h-5" /></div>
          <h3 className="font-semibold mb-2">UK-wide delivery</h3>
          <p className="text-sm text-gray-600">Dispatched within 24 hours of a paid order. Most UK addresses receive their package within 1–3 working days.</p>
        </Card>
        <Card>
          <div className="w-10 h-10 rounded-xl bg-[#064E3B]/10 text-[#064E3B] flex items-center justify-center mb-3"><Package className="w-5 h-5" /></div>
          <h3 className="font-semibold mb-2">Shop pick-up</h3>
          <p className="text-sm text-gray-600">Free. Choose "Shop pick-up" at checkout — we'll call you when your order is ready to collect from 49 Westoe Rd, NE33 4LU.</p>
        </Card>
      </div>
    </StaticPage>
  );
}

/* ─────────────────────── FAQS ─────────────────────── */
export function FAQsPage() {
  const faqs = [
    { q: 'How long does delivery take?', a: 'Standard UK delivery is 1–3 working days once payment is confirmed. Local (DH/SR/NE) orders are often next-day. Shop pick-up is available the same day for most orders — we\'ll call you when it\'s ready.' },
    { q: 'Do you deliver everywhere in the UK?', a: 'Yes. Anywhere on the mainland, with weight-based pricing shown on your checkout page. Northern Ireland, Isle of Man and other offshore addresses may need a bespoke quote — contact us.' },
    { q: 'Are your products authentic?', a: 'Yes — we source directly from Nigerian and African importers and community shops. If a product ever isn\'t what you expected, tell us within 7 days and we\'ll make it right.' },
    { q: 'What payment methods do you accept?', a: 'All major cards (Visa, Mastercard, Amex) via Stripe. Apple Pay and Google Pay are also supported at checkout.' },
    { q: 'Can I return items?', a: 'Perishable and frozen goods are non-returnable for hygiene reasons. Anything else you can return within 14 days if it\'s unopened. See our Returns page for details.' },
    { q: 'How do I track my order?', a: 'You\'ll get an email with a tracking link when your parcel is dispatched. If it\'s a shop pick-up, we\'ll ring the phone number on your order.' },
    { q: 'Do you offer bulk / wholesale?', a: 'Yes. For anything over 60 kg or for restaurants, please email orders@tishofood.co.uk — we\'ll arrange a bespoke quote.' },
    { q: 'Is my payment secure?', a: 'Yes. We never see your card details — everything is handled by Stripe (PCI-DSS compliant). The site is served over HTTPS with modern security headers.' },
  ];
  return (
    <StaticPage eyebrow="Help" title="Frequently asked questions" subtitle="Everything you need to know before you order. Still stuck? Head to the Contact page.">
      <div className="space-y-2" data-testid="faq-list">
        {faqs.map((f, i) => (
          <FaqItem key={i} question={f.q} answer={f.a} testid={`faq-item-${i}`} />
        ))}
      </div>
    </StaticPage>
  );
}
function FaqItem({ question, answer, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50"
      >
        <span className="font-medium text-[#064E3B]">{question}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-gray-600 leading-relaxed">{answer}</div>}
    </div>
  );
}

/* ─────────────────────── PRIVACY ─────────────────────── */
export function PrivacyPage() {
  return (
    <StaticPage eyebrow="Legal" title="Privacy policy" subtitle="This page explains what we collect, why, and how you can control it.">
      <Card className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
        <p><strong>Last updated:</strong> February 2026</p>
        <p>TishoFood ("we", "us", "our") respects your privacy. This policy applies to the services offered on tishofood.co.uk.</p>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">What we collect</h3>
        <ul className="list-disc pl-5">
          <li>Account details: name, email, hashed password (or Google sign-in identifier).</li>
          <li>Order details: items, delivery address, phone number, order history.</li>
          <li>Payment info: handled entirely by Stripe — we never see full card numbers.</li>
          <li>Basic technical logs: IP address, browser, pages visited (for security and troubleshooting).</li>
        </ul>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Why we use it</h3>
        <ul className="list-disc pl-5">
          <li>To process your orders and communicate with you about them.</li>
          <li>To improve the site (aggregated, non-personal analytics).</li>
          <li>To meet our legal obligations (tax, fraud prevention).</li>
        </ul>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Who we share it with</h3>
        <ul className="list-disc pl-5">
          <li>Stripe (payment processing).</li>
          <li>The courier carrying your parcel.</li>
          <li>Firebase / Google (only if you use Google Sign-In).</li>
          <li>Never sold to third parties.</li>
        </ul>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Your rights (UK GDPR)</h3>
        <p>You can request access, correction, or deletion of your data at any time by emailing <a href="mailto:orders@tishofood.co.uk">orders@tishofood.co.uk</a>. We'll respond within 30 days.</p>
      </Card>
    </StaticPage>
  );
}

/* ─────────────────────── TERMS ─────────────────────── */
export function TermsPage() {
  return (
    <StaticPage eyebrow="Legal" title="Terms & conditions" subtitle="The rules that govern your use of tishofood.co.uk and the orders you place with us.">
      <Card className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
        <p><strong>Last updated:</strong> February 2026</p>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-4">Acceptance</h3>
        <p>By using this website or placing an order you agree to these terms. If you do not agree, please do not use the site.</p>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Orders and pricing</h3>
        <ul className="list-disc pl-5">
          <li>All prices are in GBP and include VAT where applicable.</li>
          <li>An order is only confirmed once payment succeeds and you receive an email confirmation.</li>
          <li>We reserve the right to cancel an order and refund you if we cannot fulfill it (e.g. stock issue or courier restriction).</li>
        </ul>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Delivery</h3>
        <p>Shipping fees are calculated live at checkout based on weight and postcode. Estimated delivery windows are guidance, not guarantees — courier delays are outside our control.</p>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Returns</h3>
        <p>Perishable goods are non-returnable. See the Returns page for full details.</p>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Liability</h3>
        <p>Our total liability is limited to the value of your order. Nothing in these terms limits your statutory consumer rights under UK law.</p>
        <h3 className="text-lg font-semibold text-[#064E3B] mt-6">Governing law</h3>
        <p>These terms are governed by the laws of England and Wales.</p>
      </Card>
    </StaticPage>
  );
}

/* ─────────────────────── RETURNS ─────────────────────── */
export function ReturnsPage() {
  return (
    <StaticPage eyebrow="Support" title="Returns & refunds" subtitle="We want you to love your order. Here's what happens if something isn't right.">
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-3"><RefreshCcw className="w-5 h-5" /></div>
          <h3 className="font-semibold mb-2">Non-perishable goods</h3>
          <p className="text-sm text-gray-600">You have 14 days from delivery to return unopened, undamaged items for a full refund. Contact us first so we can arrange the return.</p>
        </Card>
        <Card>
          <div className="w-10 h-10 rounded-xl bg-red-50 text-red-600 flex items-center justify-center mb-3"><Package className="w-5 h-5" /></div>
          <h3 className="font-semibold mb-2">Perishable / frozen items</h3>
          <p className="text-sm text-gray-600">Non-returnable for hygiene reasons — but if there's a quality issue on arrival, tell us within 24 hours with a photo and we'll refund or replace.</p>
        </Card>
      </div>
      <Card>
        <h3 className="font-semibold text-[#064E3B] mb-3">How to start a return</h3>
        <ol className="list-decimal pl-5 text-sm text-gray-700 space-y-2">
          <li>Email <a href="mailto:orders@tishofood.co.uk" className="text-[#064E3B] underline">orders@tishofood.co.uk</a> with your order number and a photo of the item.</li>
          <li>We'll reply within one business day with a return address and any labels needed.</li>
          <li>Once we receive the item, refunds are processed within 3–5 working days to your original payment method.</li>
        </ol>
      </Card>
    </StaticPage>
  );
}

/* ─────────────────────── CATEGORY (product listing) ─────────────────────── */
export function CategoryPage({ category, title, subtitle, addToCart, toggleWishlist, wishlist, showNotification, initialProducts }) {
  // If the parent (App) has already loaded /api/products, seed the state with it
  // and skip the fetch entirely so navigating from Home → /shop or /category/* is
  // instant. Only fall back to fetching when the page is opened directly.
  const [products, setProducts] = useState(() => Array.isArray(initialProducts) && initialProducts.length ? initialProducts : []);
  const [loading, setLoading] = useState(!(Array.isArray(initialProducts) && initialProducts.length));
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');

  // Keep in sync if the parent's products change (e.g. after admin edits).
  useEffect(() => {
    if (Array.isArray(initialProducts) && initialProducts.length) {
      setProducts(initialProducts);
      setLoading(false);
    }
  }, [initialProducts]);

  useEffect(() => {
    if (Array.isArray(initialProducts) && initialProducts.length) return; // already seeded
    let cancelled = false;
    setLoading(true); setErr('');
    fetch(`${API_URL}/api/products`)
      .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then((data) => { if (!cancelled) setProducts(Array.isArray(data) ? data : []); })
      .catch((e) => { if (!cancelled) setErr(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [initialProducts]);

  const filtered = useMemo(() => {
    let list = category
      ? products.filter((p) => (p.category || '').toLowerCase() === category.toLowerCase())
      : products;
    if (q.trim()) {
      const s = q.trim().toLowerCase();
      list = list.filter((p) =>
        (p.name || '').toLowerCase().includes(s) ||
        (p.category || '').toLowerCase().includes(s) ||
        (p.description || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [products, category, q]);

  return (
    <StaticPage
      eyebrow="Shop"
      title={title || category || 'All products'}
      subtitle={subtitle || 'Every item shown here comes live from our catalog. Real stock, real prices.'}
    >
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-500" data-testid="category-count">
          Showing {filtered.length} of {products.length} products
        </p>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search this category…"
          className="px-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:border-[#064E3B]"
          data-testid="category-search"
        />
      </div>

      {loading && (
        <div className="p-12 text-center text-gray-400" data-testid="category-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
        </div>
      )}
      {err && !loading && (
        <div className="p-6 rounded-2xl bg-red-50 text-red-600 text-sm" data-testid="category-error">{err}</div>
      )}
      {!loading && !err && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="category-grid">
          {filtered.map((p) => (
            <CategoryProductCard
              key={p.id}
              product={p}
              addToCart={addToCart}
              toggleWishlist={toggleWishlist}
              wishlist={wishlist}
              showNotification={showNotification}
            />
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full p-12 text-center text-gray-400 bg-white rounded-3xl border border-gray-100">
              No products match this filter.
            </div>
          )}
        </div>
      )}
    </StaticPage>
  );
}

function CategoryProductCard({ product, addToCart, toggleWishlist, wishlist, showNotification }) {
  const inWishlist = Array.isArray(wishlist) && wishlist.some((w) => w.product_id === product.id);
  const inStock = product.inStock !== false && (product.stock ?? 1) > 0;
  return (
    <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:-translate-y-1 hover:shadow-lg transition-all" data-testid={`category-product-${product.id}`}>
      <Link to={`/product/${product.id}`} className="block relative aspect-square overflow-hidden bg-gray-50">
        <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" onError={(e) => (e.target.src = '/placeholder.jpg')} />
        {!inStock && <span className="absolute top-3 left-3 px-2 py-1 rounded-full bg-black/70 text-white text-xs">Out of stock</span>}
      </Link>
      <div className="p-4">
        <Link to={`/product/${product.id}`} className="block">
          <p className="text-xs text-gray-500 mb-1">{product.category}</p>
          <h3 className="font-semibold text-gray-900 text-sm truncate mb-2">{product.name}</h3>
        </Link>
        <div className="flex items-center justify-between">
          <p className="text-[#064E3B] font-bold">£{Number(product.price || 0).toFixed(2)}</p>
          <div className="flex gap-1">
            {toggleWishlist && (
              <button onClick={() => toggleWishlist(product)} className={`p-2 rounded-xl transition ${inWishlist ? 'bg-red-50 text-red-500' : 'bg-gray-50 text-gray-400 hover:text-red-500'}`} data-testid={`category-wishlist-${product.id}`}>
                <Heart className="w-4 h-4" fill={inWishlist ? 'currentColor' : 'none'} />
              </button>
            )}
            {addToCart && inStock && (
              <button onClick={() => { addToCart(product); showNotification && showNotification(`${product.name} added to cart`); }} className="px-3 py-2 bg-[#064E3B] text-white rounded-xl text-xs font-semibold hover:bg-[#065F46]" data-testid={`category-add-${product.id}`}>
                Add
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
